import { shuffle } from './cards.js';
import { counted, reengineer, removeOption, separateOptions, withIdsOf } from './option-units.js';
export { counted, reengineer, removeOption, separateOptions, withIdsOf };
const CARD_NAME = {
    Move1: 'Move 1', Move2: 'Move 2', Move3: 'Move 3', BackUp: 'Back-Up',
    RotateLeft: 'Rotate Left', RotateRight: 'Rotate Right', UTurn: 'U-Turn',
};
/** The rotate that turns to this side. */
export function rotateToward(side) {
    return side === 'left' ? 'RotateLeft' : 'RotateRight';
}
/** The side a Rotate Left or Rotate Right names; nothing else names one. */
export function sideOf(card) {
    return card === 'RotateLeft' ? 'left' : card === 'RotateRight' ? 'right' : undefined;
}
const other = (side) => (side === 'left' ? 'right' : 'left');
/**
 * The thirteen manoeuvres that choose a side: the cards each changes, and
 * the steps it plays instead. A run-time choice in a normal game, where
 * the side is the player's answer; in turn mode a `...Turn` card paired
 * with a Rotate, which names the side.
 *
 * Slingshot and Swerve play their steps only when their condition holds
 * as the card comes up. That needs the board, so it is asked in
 * `orchestration.ts`.
 */
export const SIDE_MANOEUVRES = {
    drift: { cards: ['Move1'], steps: (_, s) => [{ card: 'Move1' }, { slide: s }] },
    enhancedSteering: {
        cards: ['Move1'], steps: (_, s) => [{ card: rotateToward(s) }, { card: 'Move1' }],
    },
    fishtail: { cards: ['Move2', 'Move3'], steps: (c, s) => [{ card: c }, { slide: s }] },
    hardTurn: {
        cards: ['Move2'],
        steps: (_, s) => [{ card: 'Move1' }, { card: rotateToward(s) }, { card: 'Move1' }],
    },
    jTurn: { cards: ['BackUp'], steps: (_, s) => [{ card: 'BackUp' }, { card: rotateToward(s) }] },
    jink: { cards: ['Move2'], steps: (_, s) => [{ card: 'Move1' }, { slide: s }] },
    kTurn: {
        cards: ['Move3'],
        steps: (_, s) => [
            { card: 'Move1' }, { card: rotateToward(s) }, { card: 'BackUp' }, { card: rotateToward(s) },
        ],
    },
    pivot: {
        cards: ['Move2'],
        steps: (_, s) => [{ card: rotateToward(s) }, { card: 'Move1' }, { card: rotateToward(other(s)) }],
    },
    serpentine: {
        cards: ['Move3'],
        steps: (_, s) => [{ card: 'Move1' }, { slide: s }, { card: 'Move1' }, { slide: other(s) }],
    },
    slingshot: { cards: ['Move3'], steps: (_, s) => [{ slide: s }, { card: 'Move2' }] },
    sweeper: {
        cards: ['Move3'],
        steps: (_, s) => [{ card: 'Move2' }, { card: rotateToward(s) }, { card: 'Move1' }],
    },
    swerve: {
        cards: ['Move2'], steps: (_, s) => [{ slide: s }, { card: 'Move2' }, { slide: other(s) }],
    },
    yTurn: {
        cards: ['Move2'],
        steps: (_, s) => [{ card: 'Move1' }, { card: rotateToward(s) }, { card: 'BackUp' }],
    },
};
/** The four Options that put two cards in a register in both modes. */
export const PAIRING_EFFECTS = new Set([
    'overloadOverride', 'dualProcessor', 'crabLegs', 'scandinavianFlick',
]);
/**
 * Whether this Option is programmed into a register with a second card:
 * the four two-card Options, and turn mode's side manoeuvres. Goes in a
 * register through `paired` on the submission, never `gadgets` or
 * `moveOptions`.
 */
export function isPairingOption(card) {
    const effect = card.effect;
    return PAIRING_EFFECTS.has(effect)
        || (card.turnMode === true && SIDE_MANOEUVRES[effect] !== undefined);
}
const MOVES = new Set(['Move1', 'Move2', 'Move3', 'BackUp']);
const MOVE_SQUARES = {
    Move1: 1, Move2: 2, Move3: 3, BackUp: 1,
};
/**
 * What is wrong with these cards in a register this Option pairs, or
 * undefined when nothing is. Order does not matter.
 */
export function pairProblem(card, cards) {
    if (card.effect === 'overloadOverride') {
        return cards.length === 0 || cards.length === 2
            ? undefined : `${card.name} takes two cards or none`;
    }
    if (cards.length !== 2)
        return `${card.name} takes two cards`;
    const types = cards.map((c) => c.type);
    const has = (t) => types.includes(t);
    const rotates = types.filter((t) => sideOf(t) !== undefined).length;
    let fits;
    switch (card.effect) {
        case 'dualProcessor':
            fits = types.some((t) => MOVES.has(t))
                && types.some((t) => t === 'UTurn' || sideOf(t) !== undefined);
            break;
        case 'crabLegs':
            fits = has('Move1') && rotates === 1;
            break;
        case 'scandinavianFlick':
            fits = has('RotateLeft') && has('RotateRight');
            break;
        default: {
            const side = SIDE_MANOEUVRES[card.effect];
            fits = side !== undefined && rotates === 1
                && types.some((t) => side.cards.includes(t));
        }
    }
    return fits ? undefined : `${card.name} cannot take ${types.map((t) => CARD_NAME[t]).join(' and ')}`;
}
/**
 * The steps a register this Option pairs plays, `cards` primary first.
 * Assumes `pairProblem` found nothing wrong.
 */
export function pairSteps(effect, cards) {
    const types = cards.map((c) => c.type);
    const rotate = types.find((t) => sideOf(t) !== undefined);
    switch (effect) {
        case 'overloadOverride':
        case 'scandinavianFlick': {
            const steps = types.map((t) => ({ card: t }));
            return effect === 'scandinavianFlick' ? [...steps, { flick: true }] : steps;
        }
        case 'dualProcessor': {
            const move = types.find((t) => MOVES.has(t));
            const turn = types.find((t) => !MOVES.has(t));
            // Back-Up counts as a Move 1, so it always comes to nothing; a Move 0
            // is skipped rather than played.
            const squares = (MOVE_SQUARES[move] ?? 0) - (turn === 'UTurn' ? 2 : 1);
            const forward = squares > 0
                ? [{ card: `Move${squares}` }] : [];
            return [...forward, { card: turn }];
        }
        case 'crabLegs':
            return [{ card: 'Move1', toward: sideOf(rotate) }];
        default: {
            const side = SIDE_MANOEUVRES[effect];
            const move = types.find((t) => side.cards.includes(t));
            return side.steps(move, sideOf(rotate));
        }
    }
}
/**
 * Every Option in the game.
 *
 * One of each: Options are a deck of distinct cards, not a pile of
 * duplicates, so a robot cannot hold two Fourth Gears.
 *
 * A card added here may need teaching to the bot: `OPTIONS-BACKLOG.md`,
 * "A new card and the bots".
 */
/** The side manoeuvres in normal mode, a run-time choice of side. */
const SIDE_CARDS = [
    {
        id: 'drift',
        name: 'Drift',
        timing: 'runTime',
        effect: 'drift',
        text: 'Your robot may choose to slide 1 square right (or left) after '
            + 'executing a Move 1. Drift will slide 1 square right (or left) in '
            + 'water. No effect on a teleporter.',
    },
    {
        id: 'enhancedSteering',
        name: 'Enhanced Steering',
        timing: 'runTime',
        effect: 'enhancedSteering',
        text: 'Your robot may choose to Rotate Right (or Rotate Left) prior to '
            + 'executing a Move 1. No effect on a teleporter.',
    },
    {
        id: 'fishtail',
        name: 'Fishtail',
        timing: 'runTime',
        effect: 'fishtail',
        text: 'Your robot may choose to slide 1 square right (or left) after '
            + 'executing a Move 2 or Move 3. No effect on a teleporter.',
    },
    {
        id: 'hardTurn',
        name: 'Hard Turn',
        timing: 'runTime',
        effect: 'hardTurn',
        text: 'Instead of executing a Move 2, your robot may choose to Move 1, '
            + 'Rotate Right (or Rotate Left), and then Move 1. '
            + 'No effect on a teleporter.',
    },
    {
        id: 'jTurn',
        name: 'J-Turn',
        timing: 'runTime',
        effect: 'jTurn',
        text: 'When executing a Back-Up, your robot may choose to Back-Up and '
            + 'then Rotate Right (or Rotate Left). No effect on a teleporter.',
    },
    {
        id: 'jink',
        name: 'Jink',
        timing: 'runTime',
        effect: 'jink',
        text: 'Instead of executing a Move 2, your robot may choose to Move 1 and '
            + 'then slide 1 square right (or left). No effect on a teleporter.',
    },
    {
        id: 'kTurn',
        name: 'K-Turn',
        timing: 'runTime',
        effect: 'kTurn',
        text: 'Instead of executing a Move 3, your robot may choose to Move 1, '
            + 'Rotate Right (or Rotate Left), Back-Up, and then Rotate in the same '
            + 'direction. No effect on a teleporter.',
    },
    {
        id: 'pivot',
        name: 'Pivot',
        timing: 'runTime',
        effect: 'pivot',
        text: 'Instead of executing a Move 2, your robot may choose to Rotate '
            + 'Right (or Rotate Left), Move 1, and then Rotate in the opposite '
            + 'direction. No effect on a teleporter.',
    },
    {
        id: 'serpentine',
        name: 'Serpentine',
        timing: 'runTime',
        effect: 'serpentine',
        text: 'Instead of executing a Move 3, your robot may choose to Move 1, '
            + 'slide 1 square Right (or Left), Move 1, and slide 1 square in the '
            + 'opposite direction. No effect on a teleporter.',
    },
    {
        id: 'slingshot',
        name: 'Slingshot',
        timing: 'runTime',
        effect: 'slingshot',
        text: 'When executing a Move 3 with a robot in the square immediately in '
            + 'front of your robot, your robot may choose to slide 1 square right '
            + '(or left), and then Move 2. No effect on a teleporter.',
    },
    {
        id: 'sweeper',
        name: 'Sweeper',
        timing: 'runTime',
        effect: 'sweeper',
        text: 'Instead of executing a Move 3, your robot may choose to Move 2, '
            + 'Rotate Right (or Rotate Left), and then Move 1. '
            + 'No effect on a teleporter.',
    },
    {
        id: 'swerve',
        name: 'Swerve',
        timing: 'runTime',
        effect: 'swerve',
        text: 'When an obstacle that would cause damage blocks your robot\'s '
            + 'path, your robot may slide 1 square right (or left), Move 2, and '
            + 'then slide back left (or right) when executing a Move 2. Obstacles '
            + 'include a space that causes damage, pits (including timed pits), '
            + 'and all tokens. If the path is not blocked, the Move 2 executes as '
            + 'normal. No effect on a teleporter.',
    },
    {
        id: 'yTurn',
        name: 'Y-Turn',
        timing: 'runTime',
        effect: 'yTurn',
        text: 'Instead of executing a Move 2, your robot may choose to Move 1, '
            + 'Rotate Right (or Rotate Left), and then Back-Up. '
            + 'No effect on a teleporter.',
    },
];
export const OPTION_CATALOG = [
    {
        id: 'fourthGear',
        name: 'Fourth Gear',
        timing: 'runTime',
        effect: 'fourthGear',
        text: 'Your robot may now choose to move forward 4 squares when it '
            + 'executes a Move 3. Priority is that of the Move 3. No effect on a '
            + 'teleporter.',
    },
    {
        id: 'brakes',
        name: 'Brakes',
        timing: 'runTime',
        effect: 'brakes',
        text: 'Your robot may now choose to move zero when it is executing a '
            + 'Move 1. Priority is that of the Move 1. No effect on a teleporter.',
    },
    {
        id: 'reverseGears',
        name: 'Reverse Gears',
        timing: 'runTime',
        effect: 'reverseGears',
        text: 'Your robot may now choose to back up 2 squares when it is '
            + 'executing a Back-Up. Priority is that of the Back-Up. No effect on '
            + 'a teleporter.',
    },
    {
        id: 'backfire',
        name: 'Backfire',
        timing: 'runTime',
        effect: 'backfire',
        text: 'Instead of executing a Move 1, your robot may choose to Back-Up. '
            + 'Priority is that of the Move 1. No effect on a teleporter.',
    },
    {
        id: 'bootlegger',
        name: 'Bootlegger',
        timing: 'runTime',
        effect: 'bootlegger',
        text: 'Instead of executing a Move 2, your robot may choose to Move 1, '
            + 'U-Turn, and then Back-Up. A Bootlegger will push robots. Priority is '
            + 'that of the Move 2. Your robot does not need to Move 1 in order to '
            + 'U-Turn. No effect on a teleporter.',
    },
    {
        id: 'emergencyBrake',
        name: 'Emergency Brake',
        timing: 'runTime',
        effect: 'emergencyBrake',
        text: 'Instead of executing a Move 2 or Move 3, your robot may choose to '
            + 'Move 0. Priority is that of the Move card. Your robot receives a '
            + 'point of damage when it uses Emergency Brake. No effect on a '
            + 'teleporter.',
    },
    {
        id: 'meltdown',
        name: 'Meltdown',
        timing: 'mandatory',
        effect: 'meltdown',
        payload: 1,
        payloadKind: 'ammo',
        text: 'When your robot is destroyed by damage, it leaves a radioactive '
            + 'waste token in its square, even if you discard this option when your '
            + 'robot is destroyed. Liquids can only be placed on open floor or open '
            + 'terrain (gravel, mud, sand, ice, water, etc.) spaces. Liquids placed '
            + 'in water will disperse to no effect. Liquids cannot be placed on '
            + 'flags.',
    },
    {
        id: 'spinOut',
        name: 'Spin Out',
        timing: 'runTime',
        effect: 'spinOut',
        text: 'Instead of revealing a program card, your robot may choose to Move 1 '
            + 'and then U-Turn. Priority is 485. A locked card stays locked. No '
            + 'effect on a teleporter.',
    },
    // Two cards in one register. Programmed in both modes: the second card
    // is the whole point, so there is nothing to ask about at run time.
    {
        id: 'overloadOverride',
        name: 'Overload Override',
        timing: 'phaseProgrammed',
        effect: 'overloadOverride',
        text: 'You may place two program cards in a single register and execute '
            + 'both in that register phase, or you may leave a register '
            + 'unprogrammed. When programming multiple cards, the primary card is '
            + 'executed first. Your robot takes a point of damage each time this '
            + 'option is used. (Note: If an empty register becomes locked, fill it '
            + 'with a random card.) No effect on a teleporter.',
    },
    {
        id: 'dualProcessor',
        name: 'Dual Processor',
        timing: 'phaseProgrammed',
        effect: 'dualProcessor',
        text: 'You may place both a movement and rotate program card in a single '
            + 'register. When resolving the movement card, move one square less '
            + 'than the movement card and then execute the rotate card. If the '
            + 'rotate card is a U-turn, move two squares less. '
            + 'No effect on a teleporter.',
    },
    {
        id: 'crabLegs',
        name: 'Crab Legs',
        timing: 'phaseProgrammed',
        effect: 'crabLegs',
        text: 'You may place a Move 1 card in the same register as a Rotate Left '
            + 'or Rotate Right card. During that phase your robot will move 1 '
            + 'square to the left or right, respectively, without rotating. '
            + 'No effect on a teleporter.',
    },
    {
        id: 'scandinavianFlick',
        name: 'Scandinavian Flick',
        timing: 'phaseProgrammed',
        effect: 'scandinavianFlick',
        text: 'You may place a Rotate Left and Rotate Right card in a single '
            + 'register. Your robot will execute the primary card, then the other, '
            + 'and then Move 1 (Move 2 if starting the register phase on dirt, '
            + 'gravel, ice, mud, or sand). No effect on a teleporter.',
    },
    // The side manoeuvres, as run-time choices. Turn mode's are at the end.
    ...SIDE_CARDS,
    {
        id: 'skid',
        name: 'Skid',
        timing: 'runTime',
        effect: 'skid',
        text: 'After executing a Rotate Right (or Rotate Left), your robot may '
            + 'choose to slide 1 square in the direction it was facing at the '
            + 'beginning of the register phase. Generally this is the same as a '
            + 'Move 1 and then Rotate Right (or Rotate Left). '
            + 'No effect on a teleporter.',
    },
    {
        id: 'donuts',
        name: 'Donuts',
        timing: 'runTime',
        effect: 'donuts',
        text: 'Instead of executing a Move 3, place a smoke token to the FRONT, '
            + 'REAR, LEFT, and RIGHT of your robot. Laser fire will not penetrate '
            + 'smoke. A robot cannot target or be targeted in or through smoke. '
            + 'No effect on a teleporter.',
    },
    {
        id: 'doubleBarrelLaser',
        name: 'Double-Barrel Laser',
        timing: 'mainLaser',
        effect: 'doubleBarrelLaser',
        text: "Your robot's main laser has been modified to fire two shots.",
    },
    {
        id: 'highPowerLaser',
        name: 'High Power Laser',
        timing: 'mainLaser',
        effect: 'highPowerLaser',
        text: "Your robot's main laser has been modified to shoot through one "
            + 'wall or robot to reach a target robot. (If you shoot through a '
            + 'robot, that robot also receives damage.)',
    },
    {
        id: 'fireControl',
        name: 'Fire Control',
        timing: 'mainLaser',
        effect: 'fireControl',
        text: "You have targeting control of your robot's main laser. When "
            + 'scoring a point of damage, you may choose to use the damage to '
            + 'lock a register of your choice or destroy a specific option on '
            + 'the target robot. A register that is already locked cannot be '
            + 'chosen.',
    },
    {
        id: 'rearLaser',
        name: 'Rear Laser',
        timing: 'additionalWeapon',
        effect: 'rearLaser',
        text: 'Your robot has a rear-firing laser in addition to its main laser.',
    },
    {
        id: 'pressorBeam',
        name: 'Pressor Beam',
        timing: 'optionalWeapon',
        effect: 'pressorBeam',
        text: "You now have the option of firing a pressor beam instead of your "
            + "robot's main laser. The pressor beam will push a target robot 1 "
            + 'square away from you.',
    },
    {
        id: 'tractorBeam',
        name: 'Tractor Beam',
        timing: 'optionalWeapon',
        effect: 'tractorBeam',
        text: "You now have the option of firing a tractor beam instead of your "
            + "robot's main laser. The tractor beam will pull a target robot 1 "
            + 'square toward you. The beam may not be used if the target robot is '
            + 'in an adjacent square.',
    },
    {
        id: 'bigGun',
        name: 'Big Gun',
        timing: 'optionalWeapon',
        effect: 'bigGun',
        payload: 5,
        payloadKind: 'ammo',
        text: "You may fire the Big Gun instead of your robot's main laser. The "
            + 'Big Gun deals 2 points of damage and pushes your robot back 1 space.',
    },
    {
        id: 'scrambler',
        name: 'Scrambler',
        timing: 'optionalWeapon',
        effect: 'scrambler',
        text: 'You now have the option of firing a scrambler instead of your '
            + "robot's main laser. The scrambler allows you to replace the program "
            + 'card in the next register of the target robot with a random one from '
            + 'the deck. This option cannot be used on the fifth register phase.',
    },
    {
        id: 'radioControl',
        name: 'Radio Control',
        timing: 'optionalWeapon',
        effect: 'radioControl',
        text: 'You now have the option of firing a radio control beam instead of '
            + "your robot's main weapon. The radio control beam can only target a "
            + 'robot within 6 squares, and it replaces that robot\'s entire program '
            + 'for the rest of the current turn with a copy of your robot\'s '
            + 'program. In cases of card priority, the target robot moves after '
            + 'your robot.',
    },
    {
        id: 'miniHowitzer',
        name: 'Mini-Howitzer',
        timing: 'optionalWeapon',
        effect: 'miniHowitzer',
        payload: 5,
        payloadKind: 'ammo',
        text: 'You now have the option of firing a mini-howitzer instead of your '
            + "robot's main laser. The mini-howitzer will cause 1 point of "
            + 'damage in addition to pushing the target robot 1 square away '
            + 'from you.',
    },
    {
        id: 'spikedWheels',
        name: 'Spiked Wheels',
        timing: 'additionalWeapon',
        effect: 'spikedWheels',
        text: 'After your robot executes a movement card, each robot adjacent '
            + "to your robot's left and right side receives a point of damage. "
            + 'Even if a robot moves away later that register phase, it still '
            + 'receives a point of damage.',
    },
    {
        id: 'rammingGear',
        name: 'Ramming Gear',
        timing: 'mandatory',
        effect: 'rammingGear',
        text: 'When your robot pushes another robot, that robot receives a '
            + "point of damage in addition to being pushed. Even if the target "
            + "robot can't be moved, it still receives a point of damage.",
    },
    {
        id: 'ablativeCoat',
        name: 'Ablative Coat',
        timing: 'mandatory',
        effect: 'ablativeCoat',
        payload: 3,
        payloadKind: 'expends',
        text: 'Your robot is now covered with a special coat that absorbs '
            + 'damage. This option is discarded when its payload is expended.',
    },
    {
        id: 'superiorArchiveCopy',
        name: 'Superior Archive Copy',
        timing: 'runTime',
        effect: 'superiorArchiveCopy',
        text: 'You will withdraw your next archive copy undamaged, even if you '
            + 'discard this option when your robot is destroyed.',
    },
    {
        id: 'oilDropper',
        name: 'Oil Dropper',
        timing: 'phaseProgrammed',
        effect: 'oilDropper',
        payload: 3,
        payloadKind: 'ammo',
        text: 'When activated, place an oil slick token in your robot\'s square. '
            + 'Liquids can only be placed on open floor or open terrain (gravel, '
            + 'mud, sand, ice, water, etc.) spaces. Liquids placed in water will '
            + 'disperse to no effect. Liquids cannot be placed on flags.',
    },
    {
        id: 'flamingOilDropper',
        name: 'Flaming Oil Dropper',
        timing: 'phaseProgrammed',
        effect: 'flamingOilDropper',
        payload: 3,
        payloadKind: 'ammo',
        text: 'When activated, place a flaming oil slick token in your robot\'s '
            + 'square. Movement on flaming oil is as oil. A robot will receive a '
            + 'point of damage when it enters the first square with flaming oil, '
            + 'and when on flaming oil during laser fire. Liquids can only be '
            + 'placed on open floor and terrain that is not water.',
    },
    {
        id: 'smokeDropper',
        name: 'Smoke Dropper',
        timing: 'phaseProgrammed',
        effect: 'smokeDropper',
        payload: 4,
        payloadKind: 'ammo',
        text: 'When activated, place a smoke token in your robot\'s square. Laser '
            + 'fire will not penetrate smoke. A robot cannot target or be targeted '
            + 'in or through smoke.',
    },
    {
        id: 'smokeGenerator',
        name: 'Smoke Generator',
        timing: 'phaseProgrammed',
        effect: 'smokeGenerator',
        payload: 8,
        payloadKind: 'ammo',
        text: 'When activated, place a smoke token to the FRONT, REAR, LEFT, and '
            + 'RIGHT of your robot. Laser fire will not penetrate smoke. A robot '
            + 'cannot target or be targeted in or through smoke. 8 smoke tokens.',
    },
    {
        id: 'spikeDropper',
        name: 'Spike Dropper',
        timing: 'phaseProgrammed',
        effect: 'spikeDropper',
        payload: 4,
        payloadKind: 'ammo',
        text: 'When activated, place a spike token in your robot\'s square. The '
            + 'next robot to rotate on or move into that square receives a point '
            + 'of damage. A spike token is destroyed when causing damage.',
    },
    {
        id: 'waterDropper',
        name: 'Water Dropper',
        timing: 'phaseProgrammed',
        effect: 'waterDropper',
        payload: 3,
        payloadKind: 'ammo',
        text: 'When activated, place a water token in your robot\'s square. Water '
            + 'dropped in dirt, grass, sand, or gravel will instead create mud.',
    },
    {
        id: 'missileLauncher',
        name: 'Missile Launcher',
        timing: 'optionalWeapon',
        effect: 'missileLauncher',
        payload: 3,
        payloadKind: 'ammo',
        text: "You may launch a missile instead of firing your robot's main "
            + 'laser. When launched, place a missile token in your robot\'s square. '
            + 'During each subsequent phase, move the missile forward 2 squares. '
            + 'If the missile collides with a robot or wall, the missile explodes '
            + 'immediately for 4 points of damage.',
    },
    {
        id: 'abortSwitch',
        name: 'Abort Switch',
        timing: 'runTime',
        effect: 'abortSwitch',
        text: 'Instead of revealing a program card, ignore it and draw a new card '
            + 'randomly from the deck. Once the Abort Switch has been activated, '
            + 'draw program cards randomly from the deck for the remainder of the '
            + 'turn.',
    },
    {
        id: 'conditional',
        name: 'Conditional',
        timing: 'runTime',
        effect: 'conditional',
        text: 'After programming your registers, you may place one of the '
            + 'remaining program cards on this option. This conditional program may '
            + 'then be substituted for any program card in your registers before '
            + 'cards for that register phase are revealed. Discard the conditional '
            + 'program at the end of the turn, but keep this option.',
    },
    {
        id: 'flywheel',
        name: 'Flywheel',
        timing: 'turnProgrammed',
        effect: 'flywheel',
        text: 'After programming all five registers, you may place one of the '
            + 'remaining movement cards on this option. In a subsequent turn that '
            + 'card can be added to the program cards dealt to you.',
    },
    {
        id: 'mineLayer',
        name: 'Mine Layer',
        timing: 'phaseProgrammed',
        effect: 'mineLayer',
        payload: 3,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a mine token in "
            + "your robot's square. If a robot passes over or stops on the mine, "
            + 'the mine explodes for 4 points of damage.',
    },
    {
        id: 'proximityMine',
        name: 'Proximity Mine',
        timing: 'phaseProgrammed',
        effect: 'proximityMine',
        payload: 3,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a proximity "
            + "mine token in your robot's square. If a non-flying robot passes "
            + 'within 1 square of the mine it explodes for 4 points of damage.',
    },
    {
        id: 'gooDropper',
        name: 'Goo Dropper',
        timing: 'phaseProgrammed',
        effect: 'gooDropper',
        payload: 3,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a goo token in "
            + "your robot's square. If a robot passes over or stops on the goo, the "
            + 'robot cannot leave that square until it attempts to move a total of '
            + 'four squares in any direction. If a robot is pushed while on the '
            + 'goo, that counts towards the four squares. Robots may rotate freely '
            + 'on goo.',
    },
    {
        id: 'portableTeleporter',
        name: 'Portable Teleporter',
        timing: 'phaseProgrammed',
        effect: 'portableTeleporter',
        payload: 1,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a portable "
            + "teleporter token in your robot's square. Treat the portable "
            + 'teleporter as if it were a teleporter board element. A portable '
            + 'teleporter is active for all robots the moment it is launched.',
    },
    {
        id: 'bridgeLayer',
        name: 'Bridge Layer',
        timing: 'phaseProgrammed',
        effect: 'bridgeLayer',
        payload: 2,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a bridge "
            + "token in the square in front of your robot. Treat all robots "
            + 'moving over this square as if they were moving over open floor '
            + 'for the rest of the game. The placement of the bridge is blocked '
            + 'by walls and repulsor fields. Bridges may only be placed over '
            + 'pits (incl. timed pits, mine shafts, and whirlpools).',
    },
    {
        id: 'scramblerBomb',
        name: 'Scrambler Bomb',
        timing: 'phaseProgrammed',
        effect: 'scramblerBomb',
        payload: 1,
        payloadKind: 'ammo',
        text: "Before activating your robot's program card, place a scrambler "
            + "token in your robot's square. At the beginning of the next turn, all "
            + 'robots within 6 squares of the bomb execute program cards at random '
            + 'for the entire turn.',
    },
    {
        id: 'seismicCharge',
        name: 'Seismic Charge',
        timing: 'phaseProgrammed',
        effect: 'seismicCharge',
        payload: 3,
        payloadKind: 'ammo',
        text: "When activated, place a seismic charge token in your robot's "
            + 'square. The charge detonates just prior to the end of the NEXT turn. '
            + 'Walls and elevation do not block damage from a seismic charge. The '
            + 'detonation also creates a debris token in the square. 3 points of '
            + 'damage.',
    },
    {
        id: 'extraMemory',
        name: 'Extra Memory',
        timing: 'mandatory',
        effect: 'extraMemory',
        text: 'Your robot receives one extra program card per turn. This option '
            + 'does not prevent your robot from being destroyed when it has '
            + 'received 10 points of damage.',
    },
    {
        id: 'recompile',
        name: 'Recompile',
        timing: 'runTime',
        effect: 'recompile',
        text: 'You may receive a new hand of cards once per turn before your '
            + 'robot is programmed. Your robot takes a point of damage after you '
            + 'receive the new hand.',
    },
    {
        id: 'bioOption',
        name: 'Bio Option',
        timing: 'mandatory',
        effect: 'bioOption',
        text: 'When you receive this option, immediately take another option and '
            + 'place it on Bio Option. Each time your robot powers down or withdraws '
            + 'an archive copy, discard the other option and draw another.',
    },
    {
        id: 'reengineeringUnit',
        name: 'Reengineering Unit',
        timing: 'runTime',
        effect: 'reengineeringUnit',
        text: 'When your robot pushes another robot, you may exchange this option '
            + 'for an option on the other robot.',
    },
    {
        id: 'circuitBreaker',
        name: 'Circuit Breaker',
        timing: 'mandatory',
        effect: 'circuitBreaker',
        text: 'Any time your robot ends a turn with 3 or more points of damage, '
            + 'it will automatically begin the next turn powered down.',
    },
    {
        id: 'gyroscopicStabilizer',
        name: 'Gyroscopic Stabilizer',
        timing: 'turnProgrammed',
        effect: 'gyroscopicStabilizer',
        text: 'On any turn you choose to activate this option, your robot is '
            + 'not rotated by board elements (e.g., gears or rotating conveyor '
            + 'belts).',
    },
    {
        id: 'allTerrainDrive',
        name: 'All Terrain Drive',
        timing: 'turnProgrammed',
        effect: 'allTerrainDrive',
        text: "On any turn you choose to activate this option, your robot's "
            + 'movement is unaffected by terrain: gravel, ice, '
            + 'mud, oil & flaming oil, sand, and water. Current will move a '
            + 'robot with All Terrain Drive. All Terrain Drive does not prevent '
            + 'damage. Falling off a ledge into water still causes 1 damage.',
    },
    {
        id: 'submersible',
        name: 'Submersible',
        timing: 'turnProgrammed',
        effect: 'submersible',
        text: 'On any turn you choose to activate this option, your robot cannot '
            + 'be targeted, affected by weapons, or hit by '
            + 'flying objects when in water. Your robot cannot fire its weapons '
            + 'when in water. Your robot can still push and be pushed.',
    },
    {
        id: 'suspension',
        name: 'Suspension',
        timing: 'mandatory',
        effect: 'suspension',
        text: 'Your robot now has a suspension that reduces damage received by '
            + '1 when falling off a ledge, cliff, or stunt ramp.',
    },
    {
        id: 'powerDownShield',
        name: 'Power Down Shield',
        timing: 'mandatory',
        effect: 'powerDownShield',
        text: "When your robot powers down, a shield comes out on each side of "
            + "the robot's four sides. Each shield protects the robot from 1 point "
            + 'of damage per register phase. When the robot powers up, the shield '
            + 'retracts. (Note: The Power Down Shield does not block against '
            + 'Radiation or any other damage which is non-directional.)',
    },
    {
        id: 'shield',
        name: 'Shield',
        timing: 'turnProgrammed',
        directional: true,
        effect: 'shield',
        text: 'Your robot now has a shield that protects the robot from 1 point '
            + 'of damage per register phase. Program the direction the shield '
            + 'faces by turning the card to indicate Front, Back, Right, or Left. '
            + '(Note: The shield does not block against Radiation or any other '
            + 'damage which is non-directional.)',
    },
    {
        id: 'reflector',
        name: 'Reflector',
        timing: 'turnProgrammed',
        directional: true,
        effect: 'reflector',
        text: "On each turn, you must program the direction your robot's "
            + 'reflector faces by turning this card to indicate front, back, '
            + 'right, or left. When your robot is hit by a laser from that '
            + 'direction, your robot takes damage normally and the laser is '
            + 'reflected back to its source.',
    },
    {
        id: 'extraRegister',
        name: 'Extra Register',
        timing: 'turnProgrammed',
        effect: 'extraRegister',
        text: 'A robot with Extra Register can program a 6th register which will '
            + 'execute after the 5th register and before the end of the turn. This '
            + 'robot\'s weapons will execute for Register 6. Board elements will not '
            + 'execute for Register 6. Register 6 cannot be locked. All other '
            + 'registers must be programmed before a robot can program Register 6.',
    },
    {
        id: 'fabricator',
        name: 'Fabricator',
        timing: 'runTime',
        effect: 'fabricator',
        text: 'While your robot is damaged, each register phase it can remove 1 '
            + 'adjacent debris token from the board (before or after movement) and '
            + 'remove a point of damage. Your robot must be on the same level and '
            + 'not the same square as the debris.',
    },
    {
        id: 'suctionCups',
        name: 'Suction Cups',
        timing: 'mandatory',
        effect: 'suctionCups',
        payload: 1,
        payloadKind: 'ammo',
        text: 'Your robot may move up or down a ledge as normal movement. If your '
            + 'robot falls into a pit, it will receive 2 points of damage and '
            + 'reappear at the beginning of the next turn in a vacant square next '
            + 'to the pit on the same level. If there are no vacant squares, your '
            + 'robot will reappear within 2 squares of the pit. Suction Cups will '
            + 'not work on whirlpools or a blank edge of the board. 1 Fuel.',
    },
    {
        id: 'pitMove',
        name: 'Pit Move',
        timing: 'runTime',
        effect: 'pitMove',
        text: 'When your robot pushes another robot, your robot may choose to '
            + 'force the other robot to immediately U-Turn (even if the target '
            + 'robot was not moved). Precision Immobilization Technique. No effect '
            + 'on a teleporter.',
    },
    {
        id: 'shunt',
        name: 'Shunt',
        timing: 'runTime',
        effect: 'shunt',
        text: 'Your robot may choose to push a robot 1 square to the right (or '
            + 'left) instead of pushing it forward (even if the target robot was '
            + 'not moved). No effect on a teleporter.',
    },
    {
        id: 'transverseBeam',
        name: 'Transverse Beam',
        timing: 'optionalWeapon',
        effect: 'transverseBeam',
        text: 'Your robot may choose to fire a transverse beam instead of your '
            + 'robot\'s main laser. The transverse beam will slide a target robot 1 '
            + 'square left or right.',
    },
    {
        id: 'stealthMode',
        name: 'Stealth Mode',
        timing: 'mandatory',
        effect: 'stealthMode',
        text: 'Your robot cannot be targeted from more than 3 squares away. FIRE '
            + 'CONTROL is ineffective against a robot with stealth mode. Stealth '
            + 'Mode does not prevent your robot from being damaged by lasers or '
            + 'projectiles.',
    },
    {
        id: 'empGenerator',
        name: 'EMP Generator',
        timing: 'optionalWeapon',
        effect: 'empGenerator',
        payload: 3,
        payloadKind: 'ammo',
        text: 'You now have the option of spending 1 fuel token to activate an EMP '
            + 'generator instead of your robot\'s main laser. On the next register '
            + 'phase, program cards for all robots (including your own) within 2 '
            + 'squares execute for no effect. Walls and elevation do not block the '
            + 'effect of EMP Generator. 3 fuel tokens. This option cannot be used on '
            + 'the 5th register phase.',
    },
    {
        // Programmed into registers as a gadget is, in both modes: the burst
        // before the card is deployed where gadgets are, and the second burst
        // fires instead of the main laser.
        id: 'flameThrower',
        name: 'Flame Thrower',
        timing: 'phaseProgrammed',
        effect: 'flameThrower',
        payload: 3,
        payloadKind: 'ammo',
        text: 'When activated for 1 fuel token, before movement your robot will '
            + 'cause a point of damage to all robots in the first 2 squares in front '
            + 'of it. Priority is that of the movement card.\n\n'
            + 'When activated, instead of firing your robot\'s main laser, '
            + 'your robot will also cause a point of damage to all robots in the '
            + 'first 2 squares in front of it. Smoke does not block Flame Thrower. '
            + 'Flame Thrower is not affected by water. Damage before movement is '
            + 'not laser fire. 3 fuel tokens.',
    },
    {
        id: 'hydraulicBumpers',
        name: 'Hydraulic Bumpers',
        timing: 'turnProgrammed',
        directional: true,
        effect: 'hydraulicBumpers',
        text: 'Program the direction the hydraulic bumpers face by turning this '
            + 'card to indicate FRONT, BACK, RIGHT, or LEFT. When pushed from '
            + 'that direction, your robot will reduce the distance pushed by 1 '
            + 'square.',
    },
    {
        id: 'turret',
        name: 'Turret',
        timing: 'turnProgrammed',
        directional: true,
        effect: 'turret',
        text: 'Your robot now has a turret for its main laser and optional '
            + 'weapons. On each turn, you must program the direction the turret '
            + 'faces by turning this card to indicate front, back, right, or '
            + 'left. (Note: The turret does not affect additional weapons.)',
    },
    // Turn mode's fourteen. Each takes its original's name and effect, so the
    // code that resolves the original resolves these; what differs is that
    // they are set while programming and never asked about. The seven move
    // options and the Big Gun are programmed into registers (`moveOptions`
    // on the submission); the rest are switched on for the turn.
    {
        id: 'fourthGearTurn',
        name: 'Fourth Gear',
        timing: 'phaseProgrammed',
        effect: 'fourthGear',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, your robot moves forward 4 squares when it executes a '
            + 'Move 3. Priority is that of the Move 3. No effect on a teleporter.',
    },
    {
        id: 'brakesTurn',
        name: 'Brakes',
        timing: 'phaseProgrammed',
        effect: 'brakes',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, your robot moves zero when it executes a Move 1. '
            + 'Priority is that of the Move 1. No effect on a teleporter.',
    },
    {
        id: 'reverseGearsTurn',
        name: 'Reverse Gears',
        timing: 'phaseProgrammed',
        effect: 'reverseGears',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, your robot backs up 2 squares when it executes a '
            + 'Back-Up. Priority is that of the Back-Up. No effect on a teleporter.',
    },
    {
        id: 'backfireTurn',
        name: 'Backfire',
        timing: 'phaseProgrammed',
        effect: 'backfire',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, your robot Backs-Up instead of executing a Move 1. '
            + 'Priority is that of the Move 1. No effect on a teleporter.',
    },
    {
        id: 'bootleggerTurn',
        name: 'Bootlegger',
        timing: 'phaseProgrammed',
        effect: 'bootlegger',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, instead of executing a Move 2, your robot Moves 1, '
            + 'U-Turns, and then Back-Up. A Bootlegger will push robots. Priority '
            + 'is that of the Move 2. Your robot does not need to Move 1 in order '
            + 'to U-Turn. No effect on a teleporter.',
    },
    {
        id: 'emergencyBrakeTurn',
        name: 'Emergency Brake',
        timing: 'phaseProgrammed',
        effect: 'emergencyBrake',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, instead of executing a Move 2 or Move 3, your robot '
            + 'Moves 0. Priority is that of the Move card. Your robot receives a '
            + 'point of damage when it uses Emergency Brake. No effect on a '
            + 'teleporter.',
    },
    {
        id: 'skidTurn',
        name: 'Skid',
        timing: 'phaseProgrammed',
        effect: 'skid',
        turnMode: true,
        text: 'After executing a Rotate Right (or Rotate Left), your robot may '
            + 'choose to slide 1 square in the direction it was facing at the '
            + 'beginning of the register phase. Generally this is the same as a '
            + 'Move 1 and then Rotate Right (or Rotate Left). '
            + 'No effect on a teleporter.',
    },
    {
        id: 'donutsTurn',
        name: 'Donuts',
        timing: 'phaseProgrammed',
        effect: 'donuts',
        turnMode: true,
        text: 'Instead of executing a Move 3, place a smoke token to the FRONT, '
            + 'REAR, LEFT, and RIGHT of your robot. Laser fire will not penetrate '
            + 'smoke. A robot cannot target or be targeted in or through smoke. No '
            + 'effect on a teleporter.',
    },
    {
        id: 'spinOutTurn',
        name: 'Spin Out',
        timing: 'phaseProgrammed',
        effect: 'spinOut',
        turnMode: true,
        text: 'Program this option into the registers you choose. In those '
            + 'registers, instead of revealing its program card, your robot Moves 1 '
            + 'and then U-Turns. Priority is 485. A locked card stays locked. No '
            + 'effect on a teleporter.',
    },
    {
        id: 'fabricatorTurn',
        name: 'Fabricator',
        timing: 'mandatory',
        effect: 'fabricator',
        turnMode: true,
        text: 'While your robot is damaged, each register phase it removes 1 '
            + 'adjacent debris token from the board, before movement if it can and '
            + 'after movement if not, and removes a point of damage: the debris in '
            + 'front of it if there is some, otherwise one at random. Your robot '
            + 'must be adjacent to the debris.',
    },
    {
        id: 'pitMoveTurn',
        name: 'Pit Move',
        timing: 'turnProgrammed',
        effect: 'pitMove',
        turnMode: true,
        text: 'Switch this on when you program your registers. For the rest of '
            + 'the turn, a robot your robot pushes immediately U-Turns (even if it '
            + 'was not moved), except in a register where a move option changes '
            + 'your card. Precision Immobilization Technique. No effect on a '
            + 'teleporter.',
    },
    {
        id: 'reengineeringUnitTurn',
        name: 'Reengineering Unit',
        timing: 'turnProgrammed',
        effect: 'reengineeringUnit',
        turnMode: true,
        text: 'Switch this on when you program your registers. In each register, '
            + 'the first robot your robot pushes gives up an option at random, and '
            + 'this option goes to it in exchange.',
    },
    {
        id: 'shuntTurn',
        name: 'Shunt',
        timing: 'turnProgrammed',
        effect: 'shunt',
        turnMode: true,
        text: 'Switch this on when you program your registers, and choose left '
            + 'or right. For the rest of the turn, your robot pushes a robot 1 '
            + 'square that way instead of forward (even if it was not moved), '
            + 'except in a register where a move option changes your card. No '
            + 'effect on a teleporter.',
    },
    {
        id: 'transverseBeamTurn',
        name: 'Transverse Beam',
        timing: 'turnProgrammed',
        effect: 'transverseBeam',
        turnMode: true,
        text: 'Choose this as your weapon when you program your registers, and '
            + 'whether it slides to your robot\'s left or right. For the rest of '
            + 'the turn it fires instead of your robot\'s main laser, and the '
            + 'transverse beam will slide a target robot 1 square that way. When '
            + 'there is no target, or the target cannot slide, your main laser '
            + 'fires instead.',
    },
    {
        id: 'empGeneratorTurn',
        name: 'EMP Generator',
        timing: 'phaseProgrammed',
        effect: 'empGenerator',
        turnMode: true,
        payload: 3,
        payloadKind: 'ammo',
        text: 'You now have the option of spending 1 fuel token to activate an EMP '
            + 'generator instead of your robot\'s main laser. On the next register '
            + 'phase, program cards for all robots (including your own) within 2 '
            + 'squares execute for no effect. Walls and elevation do not block the '
            + 'effect of EMP Generator. 3 fuel tokens. This option cannot be used on '
            + 'the 5th register phase.',
    },
    {
        id: 'bigGunTurn',
        name: 'Big Gun',
        timing: 'phaseProgrammed',
        effect: 'bigGun',
        turnMode: true,
        payload: 5,
        payloadKind: 'ammo',
        text: 'Program this option into the registers you choose. In those '
            + "registers, your robot fires the Big Gun instead of its main laser. "
            + 'The Big Gun deals 2 points of damage and pushes your robot back 1 '
            + 'space, whether or not it hits anything. In the other registers your '
            + 'robot fires as usual.',
    },
    {
        id: 'pressorBeamTurn',
        name: 'Pressor Beam',
        timing: 'turnProgrammed',
        effect: 'pressorBeam',
        turnMode: true,
        text: 'Choose this as your weapon when you program your registers. For '
            + "the rest of the turn it fires instead of your robot's main laser, "
            + 'pushing a target robot 1 square away from you. When there is no '
            + 'target, or the target cannot be pushed, your main laser fires '
            + 'instead. A robot cannot be pushed when a wall stands between it and '
            + 'the square it would move into, when the robots lined up behind it '
            + 'end at a wall, or when its Hydraulic Bumpers face your robot.',
    },
    {
        id: 'tractorBeamTurn',
        name: 'Tractor Beam',
        timing: 'turnProgrammed',
        effect: 'tractorBeam',
        turnMode: true,
        text: 'Choose this as your weapon when you program your registers. For '
            + "the rest of the turn it fires instead of your robot's main laser, "
            + 'pulling a target robot 1 square toward you. When there is no '
            + 'target, or the target is in an adjacent square, your main laser '
            + 'fires instead.',
    },
    {
        id: 'miniHowitzerTurn',
        name: 'Mini-Howitzer',
        timing: 'turnProgrammed',
        effect: 'miniHowitzer',
        turnMode: true,
        payload: 5,
        payloadKind: 'ammo',
        text: 'Choose this as your weapon when you program your registers. For '
            + "the rest of the turn it fires instead of your robot's main laser, "
            + 'causing 1 point of damage and pushing the target robot 1 square '
            + 'away from you. When there is no target, or the target cannot be '
            + 'pushed, your main laser fires instead. A robot cannot be pushed when a '
            + 'wall stands between it and the square it would move into, when the '
            + 'robots lined up behind it end at a wall, or when its Hydraulic '
            + 'Bumpers face your robot.',
    },
    {
        id: 'scramblerTurn',
        name: 'Scrambler',
        timing: 'turnProgrammed',
        effect: 'scrambler',
        turnMode: true,
        text: 'Choose this as your weapon when you program your registers. For '
            + "the rest of the turn it fires instead of your robot's main laser, "
            + 'replacing the program card in the next register of the target robot '
            + 'with a random one from the deck, even if that register is locked. '
            + 'When there is no target, and in the fifth register phase, your main '
            + 'laser fires instead.',
    },
    {
        id: 'radioControlTurn',
        name: 'Radio Control',
        timing: 'turnProgrammed',
        effect: 'radioControl',
        turnMode: true,
        text: 'Choose this as your weapon when you program your registers. For '
            + "the rest of the turn it fires instead of your robot's main laser. "
            + 'It can only target a robot within 6 squares, and it replaces that '
            + "robot's entire program for the rest of the current turn with a copy "
            + "of your robot's program. In cases of card priority, the target "
            + 'robot moves after your robot. When there is no target in range, when '
            + 'the target is already under your radio control, and in the fifth '
            + 'register phase, your main laser fires instead.',
    },
    {
        id: 'fireControlTurn',
        name: 'Fire Control',
        timing: 'mainLaser',
        effect: 'fireControl',
        turnMode: true,
        text: "Whenever your robot's main laser damages a robot, one point of "
            + "that damage locks one of the target's unlocked registers, chosen at "
            + 'random. If the laser passes through one robot to hit another, only '
            + 'the first is locked. If every register of the target is already '
            + 'locked, the damage is dealt as usual. Always on: a register in which '
            + 'another weapon you chose fires instead of the main laser is the '
            + 'only one it misses.',
    },
    // Turn mode's side manoeuvres. Programmed like the two-card Options: the
    // Rotate paired with the Move names the side.
    ...SIDE_CARDS.map(sideTurnCard),
];
/** A turn-mode side manoeuvre: normal mode's card, programmed with a
 * Rotate for the side. */
function sideTurnCard(card) {
    return {
        ...card,
        id: `${card.id}Turn`,
        timing: 'phaseProgrammed',
        turnMode: true,
    };
}
/**
 * The cards a game of this mode deals from, by id, in catalog order.
 *
 * The one list for each mode: the deck, the starting deal, the all-Options
 * course and the server's checks all read it. Normal mode is the
 * catalog less the turn cards. Turn mode is the catalog less every card
 * that interrupts -- the ten replaced, and the Missile Launcher, Abort
 * Switch and Conditional with no replacement -- plus the turn cards.
 */
export function optionIdsForMode(mode) {
    return OPTION_CATALOG
        .filter((o) => (mode === 'turn' ? o.turnMode || !optionInterrupts(o) : !o.turnMode))
        .map((o) => o.id);
}
/**
 * The cards each move option changes: run-time choices in a normal game,
 * programmed into registers in turn mode. One per register either way.
 */
export const MOVE_OPTION_CARDS = {
    fourthGear: ['Move3'],
    brakes: ['Move1'],
    reverseGears: ['BackUp'],
    backfire: ['Move1'],
    bootlegger: ['Move2'],
    emergencyBrake: ['Move2', 'Move3'],
    skid: ['RotateLeft', 'RotateRight'],
    donuts: ['Move3'],
    // Any card at all: it replaces the card at reveal rather than changing
    // it as it executes.
    spinOut: ['Move1', 'Move2', 'Move3', 'BackUp', 'RotateLeft', 'RotateRight', 'UTurn'],
};
/**
 * Turn mode's weapons programmed into registers like a move option. They
 * change what the robot fires, not the card, so they are not in
 * `MOVE_OPTION_CARDS` -- which would have normal mode ask about the Big
 * Gun as every card ran -- and one shares a register with a move option,
 * which the one-a-register rule does not count it against.
 */
export const REGISTER_WEAPONS = new Set(['bigGun', 'empGenerator']);
/**
 * Weapons that do their work in the registers AFTER the one they fire
 * in, and so have nothing to do on the fifth, or on Extra Register's
 * sixth: the Scrambler, Radio Control, which copies a program "for the
 * rest of the current turn", and the EMP Generator, which acts "on the
 * next register phase".
 */
export const NEXT_REGISTER_WEAPONS = new Set([
    'scrambler', 'radioControl', 'empGenerator',
]);
/** Spin Out's priority, whatever card it replaces. */
export const SPIN_OUT_PRIORITY = 485;
/** Extra Register's register, after the fifth and before the end of the turn. */
export const EXTRA_REGISTER = 6;
/**
 * Whether this robot plays register 6 this turn: it holds Extra Register,
 * is in play, and has a card there. A robot that left it empty plays as
 * one without the card.
 */
export function playsExtraRegister(robot) {
    return !robot.destroyed && !robot.poweredDown
        && holdsOption(robot.options, 'extraRegister')
        && (robot.registers?.[EXTRA_REGISTER - 1]?.cards.length ?? 0) > 0;
}
/** Whether this move option changes this card. */
export function moveOptionChanges(effect, card) {
    return MOVE_OPTION_CARDS[effect]?.includes(card) ?? false;
}
/**
 * Whether a normal-mode run-time Option can change this card as it
 * executes: a move option, or a side manoeuvre.
 */
export function runTimeOptionChanges(effect, card) {
    return moveOptionChanges(effect, card)
        || (SIDE_MANOEUVRES[effect]?.cards.includes(card) ?? false);
}
/**
 * Turn mode's move options and its Big Gun: programmed into the registers
 * they apply in, through `moveOptions` on the submission rather than
 * `gadgets`. A move option deploys nothing, and shares a register with a gadget.
 */
export function isMoveOption(card) {
    return card.turnMode === true
        && (MOVE_OPTION_CARDS[card.effect] !== undefined || REGISTER_WEAPONS.has(card.effect));
}
/**
 * The weapons a turn-mode robot chooses between while programming: at most
 * one of these may be switched on in a turn. Turn mode's Fire Control is
 * not among them: it modifies the main laser and is always on, so it works
 * in every register the main laser fires.
 */
export const TURN_WEAPONS = new Set([
    'pressorBeam', 'tractorBeam', 'miniHowitzer', 'scrambler', 'radioControl',
    'transverseBeam',
]);
/**
 * What a robot's own card does to a robot it pushes: Pit Move and Shunt.
 * One a register in a normal game, and one switched on a turn in turn
 * mode.
 */
export const PUSH_OPTIONS = new Set(['pitMove', 'shunt']);
/**
 * Whether a robot on a teleporter cannot use this Option: "if a robot is
 * on a teleporter, movement options will not work". The move options,
 * Spin Out, the side manoeuvres, the Options that pair cards, and Pit
 * Move and Shunt -- the ones `asksAtRunTime`, `manoeuvre`, the pad's own
 * branch and `resolveProgramCard`'s push refuse.
 */
export function refusedOnTeleporter(effect) {
    const e = effect;
    return MOVE_OPTION_CARDS[e] !== undefined
        || SIDE_MANOEUVRES[e] !== undefined
        || PAIRING_EFFECTS.has(e)
        || PUSH_OPTIONS.has(e);
}
/** Options that work on debris, and leave the deck in a game without it. */
export const DEBRIS_OPTIONS = new Set(['fabricator']);
/**
 * The terrain All Terrain Drive ignores, and only that terrain.
 *
 * "Your robot's movement is unaffected by terrain: gravel, ice, mud, oil
 * & flaming oil, sand, and water." A CLOSED list, taken off the card: a
 * terrain the card does not name keeps working, and the Drive must not
 * hand its holder a rule the card never granted.
 *
 * `ice` is on the card and is not a `TerrainKind`, so it cannot be named
 * here. Nothing in the library uses it; if it is ever added, add it here.
 */
export const ALL_TERRAIN_IGNORES = new Set([
    'gravel', 'mud', 'oil', 'flamingOil', 'sand', 'water',
]);
/** A course's features, read off its squares: a board's or a composed
 * grid's. */
export function courseFeatures(cells) {
    const features = {
        water: false, terrain: false, falls: false, pits: false, ledges: false,
    };
    for (const row of cells) {
        for (const cell of row) {
            if (!cell)
                continue;
            const kinds = [...(cell.terrain ?? []), ...(cell.floor ? [cell.floor.kind] : [])];
            if (kinds.includes('water'))
                features.water = true;
            if (kinds.some((k) => ALL_TERRAIN_IGNORES.has(k)))
                features.terrain = true;
            if ((cell.floor?.kind === 'pit' || cell.floor?.kind === 'trapDoorPit') && !cell.floor.whirlpool) {
                features.pits = true;
            }
            const cliffs = Object.values(cell.edges ?? {})
                .flatMap((list) => (list ?? []).filter((e) => e.kind === 'cliff'));
            if (cell.stuntRamp || cliffs.some((e) => !e.ridge))
                features.falls = true;
            if (cliffs.some((e) => !e.ramp))
                features.ledges = true;
        }
    }
    return features;
}
/** Both courses' features at once: what either has. */
export function joinFeatures(a, b) {
    return {
        water: a.water || b.water, terrain: a.terrain || b.terrain, falls: a.falls || b.falls,
        pits: a.pits || b.pits, ledges: a.ledges || b.ledges,
    };
}
/**
 * Options a course can leave with nothing to do, and what each needs of
 * the boards. Only what is printed on them: a token a dropper lays later
 * does not count.
 */
const COURSE_NEEDS = {
    submersible: (course) => course.water,
    allTerrainDrive: (course) => course.terrain,
    suspension: (course) => course.falls,
    suctionCups: (course) => course.pits || course.ledges,
};
/** Whether an Option has anything to do on this course. */
export function courseUses(effect, course) {
    return COURSE_NEEDS[effect]?.(course) ?? true;
}
/** Turn cards switched on with a side, left or right, through `aimed`. */
export const SIDED_TURN_CARDS = new Set(['transverseBeam', 'shunt']);
/**
 * The classic Options: the original game's base set, by effect, so a turn
 * card counts with its original. Everything else is RallyX Off the Grid's
 * upgrades and driving manoeuvres.
 */
export const CLASSIC_OPTIONS = new Set([
    'ablativeCoat', 'abortSwitch', 'bigGun', 'brakes', 'bridgeLayer', 'circuitBreaker',
    'conditional', 'crabLegs', 'doubleBarrelLaser', 'dualProcessor', 'extraMemory',
    'fireControl', 'flywheel', 'fourthGear', 'gooDropper', 'gyroscopicStabilizer',
    'highPowerLaser', 'mineLayer', 'miniHowitzer', 'missileLauncher', 'overloadOverride',
    'portableTeleporter', 'powerDownShield', 'pressorBeam', 'proximityMine', 'radioControl',
    'rammingGear', 'rearLaser', 'recompile', 'reflector', 'reverseGears', 'scrambler',
    'scramblerBomb', 'shield', 'superiorArchiveCopy', 'tractorBeam', 'turret',
    'bioOption', 'reengineeringUnit',
]);
/**
 * What one item of a launcher's payload does when it goes off where it
 * stands.
 *
 * An Option destroyed while its owner survives spills what is left of its
 * payload, and "all the things left explode in that square for their
 * damage value" -- one blast per item, additive, spreading and halving
 * exactly as a mine's does. A Mine Layer with two mines on it is two
 * 4-point explosions, not one.
 *
 * The value is the item's OWN damage, taken from the card that carries
 * it: a mine and a missile do 4, a seismic charge 3, fuel 2 (the project
 * owner's figure, and both oil droppers carry it), a Big Gun shell 2, a
 * howitzer shell and a spike 1. Everything absent from this table has no
 * damage value at all and is simply lost with the card -- water, goo,
 * smoke, bridges, teleporters, scrambler bombs, and the Ablative Coat's
 * charge, which is armour rather than cargo.
 */
export const PAYLOAD_DAMAGE = {
    mineLayer: 4,
    proximityMine: 4,
    missileLauncher: 4,
    seismicCharge: 3,
    oilDropper: 2,
    flamingOilDropper: 2,
    empGenerator: 2,
    bigGun: 2,
    miniHowitzer: 1,
    spikeDropper: 1,
};
/**
 * The blasts a destroyed Option lets off: one per item still on it.
 *
 * Empty for a card with nothing left, no payload at all, or a payload
 * with no damage value -- which is every case but the nine above.
 */
export function payloadBlasts(card) {
    const damage = PAYLOAD_DAMAGE[card.effect];
    if (damage === undefined)
        return [];
    const left = card.remaining ?? card.payload ?? 0;
    return Array.from({ length: Math.max(0, left) }, () => damage);
}
/**
 * Whether a turn-programmed Option is switched ON for this robot's turn.
 *
 * Two things have to be true and they are recorded separately: the robot
 * HOLDS the card (`options`), and its player ACTIVATED it when
 * programming (`activatedOptions`). Holding one and not activating it
 * does nothing, which is the whole point of a turn-programmed card --
 * the Gyroscopic Stabilizer is only sometimes what you want, since board
 * rotation is often helpful.
 *
 * `activatedOptions` is cleared at End of Turn with the other per-turn
 * state, so this answers false again next turn unless re-activated.
 */
export function hasActiveTurnOption(robot, effect) {
    const active = robot.activatedOptions ?? [];
    if (active.length === 0)
        return false;
    return (robot.options ?? []).some((o) => o.effect === effect && active.includes(o.id));
}
/**
 * A shuffled Option deck.
 *
 * `allowed` names the cards the host chose to include; omitting it means
 * every card of the game's mode. An unrecognised id is ignored rather than
 * rejected, so a saved game whose card has since been renamed still
 * starts.
 */
export function buildOptionDeck(rng, allowed, mode = 'normal') {
    const ids = allowed ?? optionIdsForMode(mode);
    const chosen = OPTION_CATALOG.filter((o) => ids.includes(o.id));
    return shuffle(chosen, rng);
}
/**
 * Takes the top Option off the deck, reshuffling the discard pile in when
 * the deck runs dry.
 *
 * As the program deck already works: an Option that leaves play -- given
 * up at a chop shop, lost with a destroyed robot, or an Ablative Coat
 * used up -- goes to the discard, and the discard becomes the new deck
 * once the deck is empty.
 *
 * There is still exactly one of each Option in the game, so both piles
 * can be empty at once: every remaining card is in somebody's hand. That
 * is the only way to run out now, and `card: null` still says so -- a
 * site that would grant an Option when there is none simply grants
 * nothing.
 */
export function drawOption(deck, discard = [], rng = Math.random) {
    let draw = deck;
    let pile = discard;
    if (draw.length === 0) {
        if (pile.length === 0)
            return { card: null, deck, discard };
        draw = shuffle(pile, rng);
        pile = [];
    }
    return {
        card: counted(draw[0]),
        deck: draw.slice(1),
        discard: pile,
    };
}
/**
 * The optional weapons a robot could fire instead of its main laser.
 *
 * A weapon out of ammo is left out: it is still held, and a chop shop can
 * refill it, but offering it as a choice this register would be offering
 * nothing.
 */
export function optionalWeapons(options) {
    return (options ?? []).filter((o) => (o.timing === 'optionalWeapon' && (o.payload === undefined || (o.remaining ?? 0) > 0)));
}
/**
 * Whether holding this Option can stop a turn mid-register to ask its
 * player something. Three pauses in `game.ts` are asked for by Options:
 * `awaitingAbortChoice` (Abort Switch, Conditional), `awaitingOptionUse`
 * (`runTimeChoiceApplies`) and `awaitingWeaponChoice` (every optional
 * weapon, and Fire Control). Change one and change this.
 */
export function optionInterrupts(card) {
    // A turn card shares its original's effect and is switched on while
    // programming, or always on, so it never stops a register.
    if (card.timing === 'turnProgrammed' || card.turnMode)
        return false;
    return card.timing === 'optionalWeapon' || INTERRUPTING_EFFECTS.has(card.effect);
}
const INTERRUPTING_EFFECTS = new Set([
    'abortSwitch', 'conditional',
    'fourthGear', 'brakes', 'reverseGears', 'backfire', 'bootlegger', 'emergencyBrake',
    'spinOut', 'fireControl', 'pitMove', 'shunt', 'fabricator',
    'drift', 'enhancedSteering', 'fishtail', 'hardTurn', 'jTurn', 'jink', 'kTurn',
    'pivot', 'serpentine', 'slingshot', 'sweeper', 'swerve', 'yTurn', 'skid', 'donuts',
    'reengineeringUnit',
]);
/**
 * Draws an Option to hand to a robot: one card, and when that card is a
 * Bio Option, the card that goes straight onto it. Empty when nothing
 * can be drawn.
 */
export function drawGrant(deck, discard = [], rng = Math.random) {
    const first = drawOption(deck, discard, rng);
    if (!first.card)
        return { cards: [], deck, discard };
    if (first.card.effect !== 'bioOption') {
        return { cards: [first.card], deck: first.deck, discard: first.discard };
    }
    const onIt = drawOption(first.deck, first.discard, rng);
    return {
        cards: onIt.card ? [first.card, { ...onIt.card, onBio: true }] : [first.card],
        deck: onIt.deck,
        discard: onIt.discard,
    };
}
/**
 * Deals the card at `at` straight off a deck, as the starting deal and a
 * joiner take theirs: its payload counted out, and for a Bio Option the
 * next card onto it. `next` is where the following deal starts.
 */
export function dealFrom(deck, at) {
    const card = deck[at];
    if (!card)
        return { cards: [], next: at };
    const onIt = card.effect === 'bioOption' ? deck[at + 1] : undefined;
    return onIt
        ? { cards: [counted(card), { ...counted(onIt), onBio: true }], next: at + 2 }
        : { cards: [counted(card)], next: at + 1 };
}
/** What a grant's event names: the card, and the card on it for a Bio
 * Option. */
export function grantNames(cards) {
    return cards[1] ? { name: cards[0].name, withName: cards[1].name } : { name: cards[0].name };
}
/**
 * A Bio Option's redraw: the card on it to the discard, then another drawn
 * onto it. Discarded FIRST, so with the deck empty the draw reshuffles it
 * back and it can return. Undefined for a robot with no Bio Option, or
 * one with nothing on it and nothing to draw.
 */
export function redrawBio(options, deck, discard, rng) {
    const held = options ?? [];
    if (!holdsOption(held, 'bioOption'))
        return undefined;
    const old = held.find((o) => o.onBio);
    // The catalog entry, as every discard is: it comes back off the deck full.
    const back = old ? OPTION_CATALOG.find((o) => o.id === old.id) : undefined;
    const pile = back ? [...discard, back] : discard;
    const drawn = drawOption(deck, pile, rng);
    // Nothing on it and nothing to draw: nothing happened.
    if (!old && !drawn.card)
        return undefined;
    const kept = held.filter((o) => !o.onBio);
    return {
        options: drawn.card ? [...kept, { ...drawn.card, onBio: true }] : kept,
        deck: drawn.deck,
        discard: drawn.discard,
        gaveUp: old,
        drawn: drawn.card ?? undefined,
    };
}
/** The Options a robot can set a card aside on when programming. */
export function cardHolders(options) {
    return (options ?? []).filter((o) => o.effect === 'conditional' || o.effect === 'flywheel');
}
/** Options switched on for a turn while programming, with nothing to aim
 * or hold and no turn card's run-time original: Gyroscopic Stabilizer, All
 * Terrain Drive, Submersible. What a scrambler bomb switches at random. */
export function programmedSwitches(options) {
    return (options ?? []).filter((o) => o.timing === 'turnProgrammed' && !o.directional
        && !o.turnMode && o.effect !== 'extraRegister' && cardHolders([o]).length === 0);
}
/** The Option holding a card of this effect, if it is holding one. */
export function heldOn(options, effect) {
    return (options ?? []).find((o) => o.effect === effect && o.heldCard);
}
/** Whether a robot is holding an Option with this effect. */
export function holdsOption(options, effect) {
    return (options ?? []).some((o) => o.effect === effect);
}
/**
 * What a chop shop can actually do for this robot, right now.
 *
 * The three things a chop shop offers are each conditional, and the test
 * for "none of them apply" lived only in the client, which rendered a
 * panel saying so. Moved here so the engine can leave such a robot out of
 * the prompt entirely and report the visit to the log instead -- and so
 * there is one definition rather than two that can drift.
 */
export function chopShopOffers(robot, register, 
/** Cards a draw could actually produce: the deck plus the discard pile,
 * since an empty deck now reshuffles the discard in. */
optionsAvailable) {
    // A powered-down robot is offered nothing. Reloading, exchanging and
    // drawing are all things the robot DOES, and a powered-down robot takes
    // no actions -- yet it was being prompted for all three while parked on
    // the square. Refused here rather than at the prompt so the one
    // definition covers everything downstream: the prompt is not raised,
    // a choice sent anyway is not honoured, and register 5's "no answer
    // means take a card" default cannot fire for a player who was never
    // asked.
    if (robot.poweredDown) {
        return {
            reloadable: [], canReload: false, canExchange: false, canDraw: false, any: false,
        };
    }
    const held = robot.options ?? [];
    // Only ammo that has actually been spent. An Ablative Coat that has
    // soaked up damage is not repaired.
    const reloadable = held.filter((o) => o.payloadKind === 'ammo' && o.remaining !== undefined && o.remaining < (o.payload ?? 0));
    const canReload = reloadable.length > 0;
    // Taking a NEW option, giving up nothing, is register 5 only.
    const canDraw = register === 5 && optionsAvailable > 0;
    const canExchange = separateOptions(held).length > 0 && optionsAvailable > 0;
    return {
        reloadable,
        canReload,
        canExchange,
        canDraw,
        any: canReload || canExchange || canDraw,
    };
}
